/**
 * Session Storage Utilities
 *
 * Utilities for persisting data during a user's session.
 * Data is cleared when the browser tab is closed.
 */

const STORAGE_PREFIX = "facilityai_";

/**
 * Get a value from session storage
 *
 * @param key - Storage key
 * @param defaultValue - Default value if key doesn't exist
 * @returns Parsed value or default
 */
export function getSessionValue<T>(key: string, defaultValue: T): T {
	try {
		const item = sessionStorage.getItem(`${STORAGE_PREFIX}${key}`);
		if (item === null) {
			return defaultValue;
		}
		return JSON.parse(item) as T;
	} catch (error) {
		console.warn(`Error reading session storage key "${key}":`, error);
		return defaultValue;
	}
}

/**
 * Set a value in session storage
 *
 * @param key - Storage key
 * @param value - Value to store
 */
export function setSessionValue<T>(key: string, value: T): void {
	try {
		sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(value));
	} catch (error) {
		console.warn(`Error writing session storage key "${key}":`, error);
	}
}

/**
 * Remove a value from session storage
 *
 * @param key - Storage key
 */
export function removeSessionValue(key: string): void {
	try {
		sessionStorage.removeItem(`${STORAGE_PREFIX}${key}`);
	} catch (error) {
		console.warn(`Error removing session storage key "${key}":`, error);
	}
}

/**
 * Clear all FacilityAI session storage values
 */
export function clearSessionValues(): void {
	try {
		const keysToRemove: string[] = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i);
			if (key?.startsWith(STORAGE_PREFIX)) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach((key) => sessionStorage.removeItem(key));
	} catch (error) {
		console.warn("Error clearing session storage:", error);
	}
}

// Storage keys for tables
export const SESSION_KEYS = {
	// Location table keys
	LOCATION_SMART_FILTERS: "location_smart_filters",
	LOCATION_SEARCH_VAL: "location_search_val",
	LOCATION_SORT_BY: "location_sort_by",
	LOCATION_SORT_ORDER: "location_sort_order",
	LOCATION_PAGE_NUM: "location_page_num",
	LOCATION_PAGE_SIZE: "location_page_size",
	// Asset table keys
	ASSET_SMART_FILTERS: "asset_smart_filters",
} as const;
